import React, { useState, useEffect, useContext } from "react";
import { Layout } from "@/components/Layout";
import { API, AuthContext } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Zap, ShieldCheck } from "lucide-react";

export default function SettingsPage() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(85);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/settings/auto-dispatch`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setEnabled(data.auto_dispatch_enabled);
      setThreshold(Math.round(data.confidence_threshold * 100));
    } catch {
      toast.error("Kunne ikke hente indstillinger");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const newThreshold = enabled ? threshold / 100 : 1.0;
      const res = await fetch(`${API}/settings/auto-dispatch`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ confidence_threshold: newThreshold }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setEnabled(data.auto_dispatch_enabled);
      setThreshold(Math.round(data.confidence_threshold * 100));
      toast.success("Indstillinger gemt");
    } catch {
      toast.error("Kunne ikke gemme indstillinger");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (checked) => {
    setEnabled(checked);
    if (!checked) {
      setThreshold(100);
    } else if (threshold >= 100) {
      setThreshold(85);
    }
  };

  if (loading) {
    return (
      <Layout title="Settings">
        <div className="flex items-center justify-center h-64 text-muted-foreground">Indlæser...</div>
      </Layout>
    );
  }

  return (
    <Layout title="Settings">
      <div className="max-w-2xl mx-auto space-y-6 p-6">
        <Card data-testid="auto-dispatch-settings-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Auto-Dispatch</CardTitle>
                <CardDescription>
                  Dispatcher automatisk items til target-systemet når confidence er over tærsklen
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-dispatch-toggle" className="text-sm font-medium">Aktiver Auto-Dispatch</Label>
                <p className="text-xs text-muted-foreground">Items med høj confidence sendes automatisk</p>
              </div>
              <Switch
                id="auto-dispatch-toggle"
                data-testid="auto-dispatch-toggle"
                checked={enabled}
                onCheckedChange={handleToggle}
                disabled={!isAdmin}
              />
            </div>

            {enabled && (
              <div className="space-y-4 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Confidence Tærskel</Label>
                  <Badge variant={threshold >= 90 ? "default" : threshold >= 80 ? "secondary" : "outline"} data-testid="threshold-badge">
                    {threshold}%
                  </Badge>
                </div>
                <Slider
                  data-testid="threshold-slider"
                  value={[threshold]}
                  onValueChange={(v) => setThreshold(v[0])}
                  min={50}
                  max={99}
                  step={1}
                  disabled={!isAdmin}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>50% (aggressiv)</span>
                  <span>99% (konservativ)</span>
                </div>
                <div className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
                  Items med confidence &ge; <strong>{threshold}%</strong> dispatches automatisk til VinylFlow.
                  Items under tærsklen kræver manuel dispatch.
                </div>
              </div>
            )}

            {!isAdmin && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md p-3">
                <ShieldCheck className="w-4 h-4" />
                Kun administratorer kan ændre disse indstillinger
              </div>
            )}

            {isAdmin && (
              <Button
                data-testid="save-settings-btn"
                onClick={saveSettings}
                disabled={saving}
                className="w-full"
              >
                {saving ? "Gemmer..." : "Gem indstillinger"}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
